import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trash2, Search } from "lucide-react";

export default function AdminBeneficiaries() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    const params = statusFilter ? `?status=${statusFilter}` : "";
    const r = await api.get(`/admin/beneficiaries${params}`);
    setItems(r.data);
  }, [statusFilter]);
  useEffect(() => { document.title = "Admin · Beneficiaries"; load(); }, [load]);

  const updateStatus = async (id, status) => {
    try { await api.put(`/admin/beneficiaries/${id}`, { verification_status: status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this beneficiary registration?")) return;
    await api.delete(`/admin/beneficiaries/${id}`); toast.success("Deleted"); load();
  };

  const filtered = items.filter((b) => {
    if (!q) return true;
    const s = (b.full_name + " " + b.email + " " + b.phone + " " + b.serial).toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div data-testid="admin-beneficiaries">
      <h1 className="font-serif text-3xl font-bold mb-2">Beneficiary registry</h1>
      <p className="text-[#0a0d12]/60 text-sm mb-6">Verify beneficiaries before they enter the empowerment selection pool.</p>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0d12]/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, serial…" className="w-full pl-9 pr-3 py-2.5 border border-black/15 rounded-md text-sm" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2.5 border border-black/15 rounded-md text-sm">
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="verified">Verified</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f8f4e8] text-left">
            <tr>
              <th className="p-3">Serial</th><th className="p-3">Name</th><th className="p-3">Contact</th>
              <th className="p-3">Purpose</th><th className="p-3">Status</th><th className="p-3">Awards</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} className="border-t border-black/5 hover:bg-[#f8f4e8]/40">
                <td className="p-3 font-mono text-xs text-[#0d1724]">{b.serial}</td>
                <td className="p-3 font-semibold">{b.full_name}</td>
                <td className="p-3 text-xs">
                  <div>{b.email}</div>
                  <div className="text-[#0a0d12]/60">{b.phone}</div>
                </td>
                <td className="p-3 text-xs capitalize">{(b.purpose || "").replace(/_/g, " ")}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${b.verification_status === "verified" ? "bg-green-100 text-green-800" : b.verification_status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-800"}`}>
                    {b.verification_status}
                  </span>
                </td>
                <td className="p-3 text-xs">{(b.previous_awards || []).length}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {b.verification_status !== "verified" && <button title="Verify" onClick={() => updateStatus(b.id, "verified")} className="p-1.5 text-green-700 hover:bg-green-50 rounded"><CheckCircle2 size={14} /></button>}
                  {b.verification_status !== "rejected" && <button title="Reject" onClick={() => updateStatus(b.id, "rejected")} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><XCircle size={14} /></button>}
                  <button title="Delete" onClick={() => remove(b.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#0a0d12]/50">No beneficiaries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
